import React from 'react';

import recordEvent from 'platform/monitoring/record-event';
import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/exports';
import localStorage from 'platform/utilities/storage/localStorage';
import {
  infoTokenExists,
  refreshIfAccessTokenExpiringSoon,
} from 'platform/utilities/oauth/utilities';
import environment from 'platform/utilities/environment';

import { isChampvaProviderClaim } from '~/platform/shared/claims-appeals';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import { getErrorStatus, UNKNOWN_STATUS } from '../utils/appeals-v2-helpers';
import {
  makeAuthRequest,
  roundToNearest,
  getUploadErrorMessage,
  formatUploadDateTime,
  showTimezoneDiscrepancyMessage,
  getTimezoneDiscrepancyMessage,
  getDocTypeDescription,
} from '../utils/helpers';
import { claimTabHref, isOnFilesView } from '../utils/claim-tab-helpers';
import { resolveUploadDestinationConfig } from '../utils/uploadDestinationConfig';
import { setPageFocus } from '../utils/page';
import { getLabel as getTrackedItemLabel } from '../utils/trackedItemContent';
import manifest from '../manifest.json';
import { ANCHOR_LINKS } from '../constants';
import {
  recordClaimLettersApiEvent,
  recordIntentToFilePageViewEvent,
  recordUploadCancelEvent,
  recordUploadFailureEvent,
  recordUploadStartEvent,
  recordUploadSuccessEvent,
} from '../utils/analytics';
import {
  BACKEND_SERVICE_ERROR,
  CANCEL_UPLOAD,
  CLEAR_ADDITIONAL_EVIDENCE_NOTIFICATION,
  CLEAR_CLAIM_DETAIL,
  CLEAR_NOTIFICATION,
  DONE_UPLOADING,
  FETCH_APPEALS_ERROR,
  FETCH_APPEALS_PENDING,
  FETCH_APPEALS_SUCCESS,
  FETCH_CLAIM_LETTERS_ERROR,
  FETCH_CLAIM_LETTERS_PENDING,
  FETCH_CLAIM_LETTERS_SUCCESS,
  FETCH_CLAIMS_ERROR,
  FETCH_CLAIMS_PENDING,
  FETCH_CLAIMS_SUCCESS,
  FETCH_FAILED_UPLOADS_ERROR,
  FETCH_FAILED_UPLOADS_PENDING,
  FETCH_FAILED_UPLOADS_SUCCESS,
  FETCH_INTENTS_TO_FILE_ERROR,
  FETCH_INTENTS_TO_FILE_PENDING,
  FETCH_INTENTS_TO_FILE_SUCCESS,
  FETCH_VES_ELIGIBILITY_ERROR,
  FETCH_VES_ELIGIBILITY_PENDING,
  FETCH_VES_ELIGIBILITY_SUCCESS,
  FETCH_VES_ELIGIBILITY_THROTTLED,
  RESET_VES_ELIGIBILITY,
  GET_CLAIM_DETAIL,
  RECORD_NOT_FOUND_ERROR,
  RESET_UPLOADS,
  SET_ADDITIONAL_EVIDENCE_NOTIFICATION,
  SET_CLAIM_DETAIL,
  SET_CLAIMS_UNAVAILABLE,
  SET_DECISION_REQUEST_ERROR,
  SET_DECISION_REQUESTED,
  SET_LAST_PAGE,
  SET_NOTIFICATION,
  SET_PROGRESS,
  SET_TYPE1_UNKNOWN_ERRORS,
  SET_UNAUTHORIZED,
  SET_UPLOADER,
  SET_UPLOAD_RESULTS,
  SET_UPLOADING,
  SUBMIT_DECISION_REQUEST,
  USER_FORBIDDEN_ERROR,
  VALIDATION_ERROR,
} from './types';

export function getClaimLetters() {
  return async dispatch => {
    const startTime = Date.now();
    dispatch({ type: FETCH_CLAIM_LETTERS_PENDING });

    try {
      // /claim_letters resolves to the letters array directly (not { data }).
      const data = await apiRequest('/claim_letters');
      dispatch({ type: FETCH_CLAIM_LETTERS_SUCCESS, data });
      recordClaimLettersApiEvent({ success: true, startTime });
    } catch (error) {
      dispatch({ type: FETCH_CLAIM_LETTERS_ERROR });
      recordClaimLettersApiEvent({
        success: false,
        error: getErrorStatus(error),
        startTime,
      });
    }
  };
}

export function setNotification(message) {
  return {
    type: SET_NOTIFICATION,
    message,
  };
}

export function setAdditionalEvidenceNotification(message) {
  return {
    type: SET_ADDITIONAL_EVIDENCE_NOTIFICATION,
    message,
  };
}

export function setType1UnknownErrors(errorFiles) {
  return {
    type: SET_TYPE1_UNKNOWN_ERRORS,
    errorFiles,
  };
}

const KNOWN_UPLOAD_ERROR_CODES = [
  'DOC_UPLOAD_DUPLICATE',
  'DOC_UPLOAD_INCORRECT_PASSWORD',
  'DOC_UPLOAD_INVALID_CLAIMANT',
];

// Helper function to handle Type 1 error classification and dispatching
function handleType1Errors(dispatch, errorFiles, hasError, claimId, useVaTabs) {
  if (errorFiles.length === 0) {
    const errorMessage = getUploadErrorMessage(hasError, claimId, useVaTabs);
    dispatch(setAdditionalEvidenceNotification(errorMessage));
    return;
  }

  // Separate known vs unknown errors
  const knownErrors = errorFiles.filter(err =>
    KNOWN_UPLOAD_ERROR_CODES.includes(err?.errors?.[0]?.detail),
  );
  const unknownErrors = errorFiles.filter(
    err => !KNOWN_UPLOAD_ERROR_CODES.includes(err?.errors?.[0]?.detail),
  );

  // If there are unknown errors, store them separately
  if (unknownErrors.length > 0) {
    dispatch(
      setType1UnknownErrors(
        unknownErrors.map(err => ({
          fileName: err.fileName,
          docType: err.docType,
        })),
      ),
    );
  }

  // If there are known errors, show the first one in additionalEvidenceMessage
  if (knownErrors.length > 0) {
    const errorMessage = getUploadErrorMessage(
      knownErrors[0],
      claimId,
      useVaTabs,
    );
    dispatch(setAdditionalEvidenceNotification(errorMessage));
  }
}

function fetchAppealsSuccess(response) {
  const appeals = response.data;
  return {
    type: FETCH_APPEALS_SUCCESS,
    appeals,
  };
}

export function getAppealsV2() {
  return dispatch => {
    dispatch({ type: FETCH_APPEALS_PENDING });
    return apiRequest('/appeals')
      .then(appeals => dispatch(fetchAppealsSuccess(appeals)))
      .catch(error => {
        const status = getErrorStatus(error);
        const action = { type: '' };
        switch (status) {
          case '403':
            action.type = USER_FORBIDDEN_ERROR;
            break;
          case '404':
            action.type = RECORD_NOT_FOUND_ERROR;
            break;
          case '422':
            action.type = VALIDATION_ERROR;
            break;
          case '502':
            action.type = BACKEND_SERVICE_ERROR;
            break;
          default:
            action.type = FETCH_APPEALS_ERROR;
            break;
        }
        return dispatch(action);
      });
  };
}

function fetchClaimsSuccess(claims) {
  return {
    type: FETCH_CLAIMS_SUCCESS,
    claims,
  };
}

const recordClaimsAPIEvent = ({ startTime, success, error }) => {
  const event = {
    event: 'api_call',
    'api-name': 'GET claims',
    'api-status': success ? 'successful' : 'failed',
  };
  if (error) {
    event['error-key'] = error;
  }
  if (startTime) {
    const apiLatencyMs = roundToNearest({
      interval: 5000,
      value: Date.now() - startTime,
    });
    event['api-latency-ms'] = apiLatencyMs;
  }

  // There is a difference between the way that custom dimensions
  // and metrics are dealt with in UA (Universal Analytics) vs in
  // GA4. In UA, we push keys with dashes ('-') but in GA4 the object
  // keys must be delimited with ('_'). So we should just include
  // both versions for the applicable keys
  Object.keys(event).forEach(key => {
    if (key.includes('-')) {
      const newKey = key.replace(/-/g, '_');
      event[newKey] = event[key];
    }
  });

  recordEvent(event);
  if (event['error-key']) {
    recordEvent({
      'error-key': undefined,
    });
  }
};

export const getClaims = () => {
  return dispatch => {
    const startTimeMillis = Date.now();
    dispatch({ type: FETCH_CLAIMS_PENDING });

    return apiRequest('/benefits_claims')
      .then(res => {
        recordClaimsAPIEvent({
          startTime: startTimeMillis,
          success: true,
        });

        dispatch(fetchClaimsSuccess(res.data));
      })
      .catch(error => {
        const errorCode = getErrorStatus(error);
        if (errorCode && errorCode !== UNKNOWN_STATUS) {
          // Error tracking removed
        }

        // This onError callback will be called with a null response arg when
        // the API takes too long to return data
        const errorDetail =
          error === null ? '504 Timed out - API took too long' : errorCode;
        recordClaimsAPIEvent({
          startTime: startTimeMillis,
          success: false,
          error: errorDetail,
        });

        return dispatch({ type: FETCH_CLAIMS_ERROR });
      });
  };
};

export const getClaim = (id, navigate, provider) => {
  return dispatch => {
    dispatch({ type: GET_CLAIM_DETAIL });

    const url = provider
      ? `/benefits_claims/${id}?type=${provider}`
      : `/benefits_claims/${id}`;

    return apiRequest(url)
      .then(res => {
        dispatch({
          type: SET_CLAIM_DETAIL,
          claim: res.data,
        });
      })
      .catch(error => {
        if (error.status !== 404 || !navigate) {
          return dispatch({
            type: SET_CLAIMS_UNAVAILABLE,
            error: error.message,
          });
        }

        return navigate('/your-claims', { replace: true });
      });
  };
};

export const clearClaim = () => ({ type: CLEAR_CLAIM_DETAIL });

export function submit5103(id, trackedItemId, cstClaimPhasesEnabled = false) {
  return dispatch => {
    dispatch({
      type: SUBMIT_DECISION_REQUEST,
    });

    const body = JSON.stringify({
      trackedItemId: Number(trackedItemId) || null,
    });

    makeAuthRequest(
      `/v0/benefits_claims/${id}/submit5103`,
      { method: 'POST', body },
      dispatch,
      () => {
        dispatch({ type: SET_DECISION_REQUESTED });
        if (cstClaimPhasesEnabled) {
          dispatch(
            setNotification({
              title: 'We received your evidence waiver',
              body: 'Thank you. We’ll move your claim to the next step as soon as possible.',
            }),
          );
        } else {
          dispatch(
            setNotification({
              title: 'Request received',
              body: 'Thank you. We have your claim request and will make a decision.',
            }),
          );
        }
      },
      error => {
        dispatch({ type: SET_DECISION_REQUEST_ERROR, error });
      },
    );
  };
}
export function resetUploads() {
  return {
    type: RESET_UPLOADS,
  };
}

export function calcProgress(
  totalFiles,
  totalSize,
  filesComplete,
  bytesComplete,
) {
  const ratio = 0.8;

  return (
    (filesComplete / totalFiles) * (1 - ratio) +
    (bytesComplete / totalSize) * ratio
  );
}

export function clearNotification() {
  return {
    type: CLEAR_NOTIFICATION,
  };
}

export function clearAdditionalEvidenceNotification() {
  return {
    type: CLEAR_ADDITIONAL_EVIDENCE_NOTIFICATION,
  };
}

export function formatApplicantList(applicants) {
  if (!applicants || applicants.length === 0) return null;
  if (applicants.length === 1) return applicants[0];
  if (applicants.length === 2) return `${applicants[0]} and ${applicants[1]}`;
  return `${applicants.slice(0, -1).join(', ')}, and ${
    applicants[applicants.length - 1]
  }`;
}

// Picks the indefinite article for a document type so the success alert reads
// "an annulment decree" rather than "a annulment decree" (#152522). Vowel-letter
// check is enough here: "Annulment decree" is the only CHAMPVA document type
// starting with a vowel, and none of them are vowel-letter/consonant-sound words
// like "university".
export function indefiniteArticle(word) {
  return /^[aeiou]/i.test(word ?? '') ? 'an' : 'a';
}

// Helper to build upload success notification message
export function buildUploadNotification(
  uploadDate,
  now,
  timezoneOffset,
  claimId,
  trackedItem,
  uploadMetadata,
  files,
  useVaTabs = false,
) {
  const isChampvaUpload = isChampvaProviderClaim({
    attributes: { provider: uploadMetadata?.provider },
  });

  if (isChampvaUpload) {
    const docType = files?.[0]?.docType?.value?.toLowerCase() ?? 'document';
    const firstNames = (files?.[0]?.applicants ?? []).map(
      name => name.split(' ')[0],
    );
    const applicantList = formatApplicantList(firstNames);
    const titleBase = `We received ${indefiniteArticle(docType)} ${docType}`;
    const title = applicantList
      ? `${titleBase} for ${applicantList}`
      : titleBase;
    return {
      title,
      body: (
        <>
          <p className="vads-u-margin-top--0">
            You submitted this file on <strong>{uploadDate}</strong>.
          </p>
          <p className="vads-u-margin-bottom--0">
            We'll review it and mail you a letter if we need more information.
          </p>
        </>
      ),
    };
  }

  const submissionsAnchor = `#${ANCHOR_LINKS.fileSubmissionsInProgress}`;
  const statusLinkHref = useVaTabs
    ? claimTabHref(claimId, 'files', { hash: submissionsAnchor })
    : `/track-claims/your-claims/${claimId}/files${submissionsAnchor}`;

  const timezoneNote = showTimezoneDiscrepancyMessage(now) ? (
    <div className="vads-u-margin-top--2 vads-u-margin-bottom--0">
      <strong>Note:</strong>{' '}
      {getTimezoneDiscrepancyMessage(timezoneOffset, now)}
    </div>
  ) : null;

  const title = trackedItem
    ? `Submission started for ${getTrackedItemLabel(trackedItem)}`
    : 'Document submission started';

  return {
    title,
    body: (
      <>
        <span>
          Your submission started on <strong>{uploadDate}</strong>. It can take
          up to 2 days for us to receive your files.
        </span>
        <va-link
          class="vads-u-display--block vads-u-margin-top--2"
          href={statusLinkHref}
          text="Check the status of your submissions"
          onClick={e => {
            // Check location at click time, not build time: the alert persists
            // across tabs, so its build location may differ from where it's clicked.
            if (isOnFilesView(window.location)) {
              e.preventDefault();
              setPageFocus(submissionsAnchor);
            }
          }}
        />
        {timezoneNote}
      </>
    ),
  };
}

// Seconds before access token expiration at which an upload refreshes it.
// Wider than apiRequest's 30s window because a batch of large files can take
// a while to finish once it starts.
export const UPLOAD_TOKEN_REFRESH_THRESHOLD_SECONDS = 60;

export async function refreshAccessTokenBeforeUpload() {
  const serviceName = sessionStorage.getItem('serviceName');
  if (!infoTokenExists() || !serviceName) return;

  try {
    await refreshIfAccessTokenExpiringSoon({
      thresholdSeconds: UPLOAD_TOKEN_REFRESH_THRESHOLD_SECONDS,
      type: serviceName,
    });
  } catch (e) {
    // Don't block the upload; a failed request will set the unauthorized flag
  }
}

// Loads fine-uploader and refreshes the access token in parallel. Resolves to
// FineUploaderBasic, or null if the user cancelled while waiting: there's no
// uploader to cancel yet, so the caller skips starting the upload.
export function loadUploaderAfterTokenRefresh(getState) {
  return Promise.all([
    import(/* webpackChunkName: "claims-uploader" */ 'fine-uploader/lib/core'),
    refreshAccessTokenBeforeUpload(),
  ]).then(([module]) =>
    getState().disability.status.uploads.uploading
      ? module.default.FineUploaderBasic
      : null,
  );
}

// Document upload function using Lighthouse endpoint

export function submitFiles(claimId, trackedItem, files, uploadMetadata = {}) {
  let filesComplete = 0;
  let bytesComplete = 0;
  let hasError = false;
  const errorFiles = []; // Collect all failed files
  // FineUploader ids of the files that failed and that uploaded. Each batch
  // gets a new uploader, so an id is the file's index in `files` and in
  // AddFilesForm's list. Cancelled files are in neither list.
  const failedIndexes = [];
  const succeededIndexes = [];
  const totalSize = files.reduce((sum, file) => sum + file.file.size, 0);
  const totalFiles = files.length;
  const trackedItemId = trackedItem ? trackedItem.id : null;
  const isFirstParty = trackedItem?.isFirstParty;
  const uploadDestinationConfig = resolveUploadDestinationConfig({
    claimId,
    trackedItemId,
    isFirstParty,
    uploadMetadata,
  });
  const uploadedFiles = [];

  // Record enhanced upload start event and get retry info for each file
  const { filesWithRetryInfo, retryFileCount } = recordUploadStartEvent({
    files,
    claimId,
  });

  return (dispatch, getState) => {
    const useVaTabs = toggleValues(getState())[
      FEATURE_FLAG_NAMES.cstClaimDetailsVaTabs
    ];
    dispatch(clearNotification());
    dispatch(clearAdditionalEvidenceNotification());
    dispatch(setType1UnknownErrors(null));
    dispatch({
      type: SET_UPLOADING,
      uploading: true,
    });
    dispatch({
      type: SET_PROGRESS,
      progress: 0,
    });
    return loadUploaderAfterTokenRefresh(getState).then(FineUploaderBasic => {
      if (!FineUploaderBasic) return;

      const csrfTokenStored = localStorage.getItem('csrfToken');
      const uploader = new FineUploaderBasic({
        request: {
          endpoint: uploadDestinationConfig.endpoint,
          inputName: 'file',
          customHeaders: {
            'Source-App-Name': manifest.entryName,
            'X-Key-Inflection': 'camel',
            'X-CSRF-Token': csrfTokenStored,
          },
        },
        cors: {
          expected: true,
          sendCredentials: true,
        },
        multiple: false,
        callbacks: {
          onAllComplete: async () => {
            if (!hasError) {
              recordUploadSuccessEvent({
                fileCount: totalFiles,
                retryFileCount,
              });
              dispatch({
                type: DONE_UPLOADING,
              });

              const now = new Date(Date.now());
              const uploadDate = formatUploadDateTime(now);
              const timezoneOffset = now.getTimezoneOffset();

              const notificationMessage = buildUploadNotification(
                uploadDate,
                now,
                timezoneOffset,
                claimId,
                trackedItem,
                uploadMetadata,
                files,
                useVaTabs,
              );

              // CHAMPVA success alerts previously rendered inside
              // AdditionalEvidencePage, below the description/OHI link. They now
              // use the same top-of-page slot in ClaimDetailLayout as every other
              // CST claim, matching where the upload failure alert already
              // renders (#151988).
              dispatch(setNotification(notificationMessage));
            } else {
              recordUploadFailureEvent({
                errorFiles,
                files,
                filesWithRetryInfo,
                claimId,
                retryFileCount,
                successFileCount: uploadedFiles.length,
              });

              // Lets AddFilesForm remove the files that uploaded, keeping failed
              // and cancelled ones. Nothing is reported if no file uploaded.
              if (succeededIndexes.length) {
                dispatch({
                  type: SET_UPLOAD_RESULTS,
                  succeededIndexes,
                  fileCount: totalFiles,
                });
              }

              // This was previously using SET_UPLOAD_ERROR, but no first party upload flow
              // used the `uploadError` value. Using CANCEL_UPLOAD instead to not affect
              // `uploadError` since it is not consumed
              dispatch({
                type: CANCEL_UPLOAD,
              });

              // Handle Type 1 errors (known vs unknown classification)
              handleType1Errors(
                dispatch,
                errorFiles,
                hasError,
                claimId,
                useVaTabs,
              );
            }
          },
          // fine-uploader sends 3 files at a time (default maxConnections), so
          // files past the first three start later and check the token again.
          // It waits on the returned promise before sending the file.
          onUpload: () => refreshAccessTokenBeforeUpload(),
          onTotalProgress: bytes => {
            bytesComplete = bytes;
            dispatch({
              type: SET_PROGRESS,
              progress: calcProgress(
                totalFiles,
                totalSize,
                filesComplete,
                bytesComplete,
              ),
            });
          },
          onComplete: (id, fileName, responseJSON) => {
            filesComplete += 1;
            // fine-uploader calls onError before onComplete for a failed file,
            // and never calls onComplete for a cancelled one
            if (!failedIndexes.includes(id)) {
              succeededIndexes.push(id);
            }
            dispatch({
              type: SET_PROGRESS,
              progress: calcProgress(
                totalFiles,
                totalSize,
                filesComplete,
                bytesComplete,
              ),
            });

            const confirmationCode =
              responseJSON?.data?.attributes?.confirmationCode;
            if (confirmationCode) {
              const matchingFile = files[id] || {};
              uploadedFiles.push({
                confirmationCode,
                name: fileName,
                attachmentId:
                  matchingFile?.docType?.value ||
                  matchingFile?.attachmentId?.value ||
                  matchingFile?.attachmentId,
                applicants: matchingFile?.applicants,
              });
            }
          },
          onError: (id, fileName, _reason, { response, status }) => {
            if (status === 401 || status === 403) {
              dispatch({
                type: SET_UNAUTHORIZED,
              });
            }
            if (status < 200 || status > 299) {
              const error = JSON.parse(response || '{}');
              error.fileName = fileName;

              // Get docType from the matching file
              const fileIndex = id;
              const matchingFile = files[fileIndex];
              if (matchingFile && matchingFile.docType) {
                try {
                  error.docType = getDocTypeDescription(
                    matchingFile.docType.value,
                  );
                } catch (e) {
                  error.docType = matchingFile.docType.value || 'Unknown';
                }
              } else {
                error.docType = 'Unknown';
              }

              errorFiles.push(error);
              failedIndexes.push(id);

              hasError = error;
            }
          },
        },
      });
      dispatch({
        type: SET_UPLOADER,
        uploader,
      });
      dispatch({
        type: SET_PROGRESS,
        progress: filesComplete / files.length,
      });

      files.forEach(fileMeta => {
        uploader.addFiles(
          fileMeta.file,
          uploadDestinationConfig.buildUploadParams(fileMeta),
        );
      });
    });
  };
}

export function cancelUpload({
  cancelFileCount,
  retryFileCount,
  batchId,
  apiName,
}) {
  return (dispatch, getState) => {
    const { uploader } = getState().disability.status.uploads;
    recordUploadCancelEvent({
      cancelFileCount,
      retryFileCount,
      batchId,
      apiName,
    });

    if (uploader) {
      uploader.cancelAll();
    }

    dispatch({
      type: CANCEL_UPLOAD,
    });
  };
}

export function setLastPage(page) {
  return {
    type: SET_LAST_PAGE,
    page,
  };
}

export function fetchFailedUploads() {
  return async dispatch => {
    dispatch({ type: FETCH_FAILED_UPLOADS_PENDING });

    try {
      const response = await apiRequest(
        '/benefits_claims/failed_upload_evidence_submissions',
      );
      dispatch({
        type: FETCH_FAILED_UPLOADS_SUCCESS,
        data: response.data,
      });
    } catch (error) {
      dispatch({
        type: FETCH_FAILED_UPLOADS_ERROR,
        error: error.message || 'Failed to fetch failed uploads',
      });
    }
  };
}

const sortByCreationDate = itfs =>
  [...itfs].sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));

export function getIntentsToFile() {
  return async dispatch => {
    const startTime = Date.now();

    dispatch({ type: FETCH_INTENTS_TO_FILE_PENDING });

    try {
      const response = await apiRequest('/intents_to_file');
      const intentsToFile = sortByCreationDate(response?.data || []);

      dispatch({
        type: FETCH_INTENTS_TO_FILE_SUCCESS,
        data: intentsToFile,
      });
      recordIntentToFilePageViewEvent({
        intentsToFile,
        latencyMs: Date.now() - startTime,
      });
    } catch (error) {
      dispatch({ type: FETCH_INTENTS_TO_FILE_ERROR });
      recordIntentToFilePageViewEvent({
        latencyMs: Date.now() - startTime,
        errorKey: getErrorStatus(error),
      });
    }
  };
}

export const resetVesEligibility = () => ({ type: RESET_VES_ELIGIBILITY });

export const fetchVesEligibility = (id, provider) => {
  return async dispatch => {
    dispatch({ type: FETCH_VES_ELIGIBILITY_PENDING });

    try {
      const response = await apiRequest(
        `${environment.API_URL}/ivc_champva/v1/forms/champva_eligibility`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // eslint-disable-next-line camelcase
          body: JSON.stringify({ form_uuids: [id] }),
        },
      );

      dispatch({ type: FETCH_VES_ELIGIBILITY_SUCCESS, data: response.data });

      // Re-fetch the claim silently so updated eligibility data from
      // CstChampvaApplicants is reflected in Redux. We do NOT use the shared
      // getClaim action here because its error handler dispatches
      // SET_CLAIMS_UNAVAILABLE, which would wipe an already-rendered page on
      // any transient failure. Instead we dispatch only SET_CLAIM_DETAIL on
      // success and silently ignore errors to keep the page stable.
      const url = provider
        ? `/benefits_claims/${id}?type=${provider}`
        : `/benefits_claims/${id}`;
      try {
        const claimRes = await apiRequest(url);
        dispatch({ type: SET_CLAIM_DETAIL, claim: claimRes.data });
      } catch (_claimErr) {
        // Intentionally silent — the page already rendered with valid claim
        // data. A re-fetch failure here should leave the page stale rather
        // than destroy it.
      }
    } catch (error) {
      if (error.status === 429) {
        dispatch({ type: FETCH_VES_ELIGIBILITY_THROTTLED });
      } else {
        dispatch({ type: FETCH_VES_ELIGIBILITY_ERROR, error: error.message });
      }
    }
  };
};
